import React, { useRef, useEffect } from 'react';
import { TimeFrameOption, GroupByOption } from '../../../services/marketPerformanceService';

interface CustomDateRange {
  startDate: Date | null;
  endDate: Date | null;
}

interface ChartControlsProps {
  timeFrameOptions: TimeFrameOption[];
  groupByOptions: GroupByOption[];
  selectedTimeFrame: TimeFrameOption;
  selectedGroupBy: GroupByOption;
  isCustomRangeSelected: boolean;
  customRange: CustomDateRange;
  showDatePicker: boolean;
  onTimeFrameSelect: (timeFrame: TimeFrameOption) => void;
  onGroupBySelect: (groupBy: GroupByOption) => void;
  onCustomRangeToggle: () => void;
  onCustomRangeApply: () => void;
  onCustomRangeCancel: () => void;
  onCustomRangeChange: (field: 'startDate' | 'endDate', value: Date | null) => void;
  formatDateInput: (date: Date | null) => string;
  getCustomRangeLabel: () => string;
}

export const ChartControls: React.FC<ChartControlsProps> = ({
  timeFrameOptions,
  groupByOptions,
  selectedTimeFrame,
  selectedGroupBy,
  isCustomRangeSelected,
  customRange,
  showDatePicker,
  onTimeFrameSelect,
  onGroupBySelect,
  onCustomRangeToggle,
  onCustomRangeApply,
  onCustomRangeCancel,
  onCustomRangeChange,
  formatDateInput,
  getCustomRangeLabel,
}) => {
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        onCustomRangeCancel();
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker, onCustomRangeCancel]);

  return (
    <div className="w-36 pl-4 text-sm flex-shrink-0">
      <h4 className="font-semibold mb-2">Time Frame</h4>
      <div className="space-y-1 mb-4">
        {timeFrameOptions.map((option) => (
          <div key={option.value}>
            <button
              type="button"
              onClick={() => onTimeFrameSelect(option)}
              className={`text-left w-full hover:underline ${
                !isCustomRangeSelected && selectedTimeFrame.value === option.value
                  ? 'text-green-600 font-bold'
                  : 'text-gray-500'
              }`}
            >
              {option.label}
            </button>
          </div>
        ))}
        <div className="relative" ref={datePickerRef}>
          <button
            type="button"
            onClick={onCustomRangeToggle}
            className={`text-left w-full hover:underline ${
              isCustomRangeSelected ? 'text-green-600 font-bold' : 'text-gray-500'
            }`}
          >
            {getCustomRangeLabel()}
          </button>

          {showDatePicker && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-300 rounded shadow-lg p-3 z-50 w-64">
              <div className="mb-2">
                <label htmlFor="start-date" className="block text-xs text-gray-600 mb-1">
                  Start Date
                </label>
                <input
                  id="start-date"
                  type="date"
                  value={formatDateInput(customRange.startDate)}
                  max={formatDateInput(new Date())}
                  onChange={(e) => onCustomRangeChange('startDate', e.target.value ? new Date(e.target.value) : null)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  aria-label="Start Date"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="end-date" className="block text-xs text-gray-600 mb-1">
                  End Date
                </label>
                <input
                  id="end-date"
                  type="date"
                  value={formatDateInput(customRange.endDate)}
                  max={formatDateInput(new Date())}
                  onChange={(e) => onCustomRangeChange('endDate', e.target.value ? new Date(e.target.value) : null)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  aria-label="End Date"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onCustomRangeCancel}
                  className="flex-1 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onCustomRangeApply}
                  disabled={!customRange.startDate || !customRange.endDate}
                  className="flex-1 px-3 py-1 text-sm text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <h4 className="font-semibold mb-2">Group By</h4>
      <div className="space-y-1">
        {groupByOptions.map((option) => (
          <div key={option.value}>
            <button
              type="button"
              onClick={() => onGroupBySelect(option)}
              className={`text-left w-full hover:underline ${
                selectedGroupBy.value === option.value ? 'text-green-600 font-bold' : 'text-gray-500'
              }`}
            >
              {option.label}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
